/**
 * VariationCandidates — swipeable candidate bubbles surfaced by "New variation"
 * (spec §5.3 + §8 row f). The candidates render WITHOUT writing back to the
 * post; only the user's pick commits (→ usePostMediaEdit.pickCandidate). Default
 * is n:1 (Phase 3 cost governance), but 2–4 render here when the user opts up.
 *
 * AI-temporary contract (§2.6): dashed-skill frame, SparkleIcon provenance,
 * "Use this" = bg-skill/10 text-skill, "Discard" = monochrome neutral exit.
 * One active bubble at a time with ‹ › swipe (keyboard + click); a dot strip
 * shows position. Skeleton shaped to the media aspect while the pick commits.
 */
import {
  CaretLeftIcon,
  CaretRightIcon,
  CheckIcon,
  SparkleIcon,
  XIcon,
} from '@phosphor-icons/react';
import { useState } from 'react';

import type { VariationCandidate } from './usePostMediaEdit';

import { Button } from '@/components/ui/button';
import { CircleIconButton } from '@/components/ui/circle-icon-button';
import { MOBILE_HIT_AREA } from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';

export interface VariationCandidatesProps {
  items: VariationCandidate[];
  busy: boolean;
  onPick(candidate: VariationCandidate): void;
  onDismiss(): void;
  /**
   * Opt-in to the full n:1→4 range (README §1: "n:1 by default, opt-in to
   * more" — cost governance, not a missing feature). Offered only while
   * exactly one candidate is showing; once the user has already opted up,
   * re-rolling a second batch has no obvious "which one" semantics, so the
   * affordance disappears rather than inviting a repeat cost.
   */
  onSeeMore?(): void;
}

export function VariationCandidates({
  items,
  busy,
  onPick,
  onDismiss,
  onSeeMore,
}: VariationCandidatesProps) {
  const [active, setActive] = useState(0);
  if (items.length === 0) return <></>;
  const clamp = (n: number) => Math.max(0, Math.min(items.length - 1, n));
  // Derive the safe index once so the view can never render out of range when
  // `items` shrinks after a re-variation (e.g. was at index 3, new batch has 2).
  const activeIdx = clamp(active);
  const current = items[activeIdx];
  const multi = items.length > 1;

  return (
    <div className="space-y-2 rounded-xl border border-dashed border-skill/30 bg-skill/[0.04] p-2.5">
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-1.5 text-detail font-medium text-skill/80">
          <SparkleIcon className="size-3 text-skill/70" weight="duotone" aria-hidden />
          {multi ? `Variation ${activeIdx + 1} of ${items.length}` : 'Variation'}
        </span>
        <CircleIconButton
          size="sm"
          ariaLabel="Dismiss candidates"
          onClick={onDismiss}
          className="text-muted-foreground/50 hover:bg-muted/50 hover:text-foreground"
        >
          <XIcon className="size-3" weight="bold" />
        </CircleIconButton>
      </div>
      <div className="relative">
        {busy ? (
          <div
            className="aspect-square w-full animate-pulse rounded-lg bg-skill/10 motion-reduce:animate-none"
            aria-busy="true"
            aria-hidden
          />
        ) : (
          <>
            {items.map((item, i) => (
              <img
                key={item.url}
                src={item.url}
                alt={`Variation ${i + 1}`}
                loading="lazy"
                className={cn(
                  'aspect-square w-full rounded-lg border border-dashed border-skill/40 object-cover',
                  i !== activeIdx && 'hidden',
                )}
              />
            ))}
          </>
        )}
        {multi && !busy ? (
          <>
            <button
              type="button"
              aria-label="Previous variation"
              disabled={busy}
              onClick={() => setActive((a) => clamp(a - 1))}
              className={cn(
                MOBILE_HIT_AREA,
                'absolute left-1 top-1/2 inline-flex size-6 -translate-y-1/2 items-center justify-center rounded-full bg-background/85 text-foreground/80 shadow-elevation-1 backdrop-blur-sm transition-colors hover:bg-background',
              )}
            >
              <CaretLeftIcon className="size-3.5" weight="bold" />
            </button>
            <button
              type="button"
              aria-label="Next variation"
              disabled={busy}
              onClick={() => setActive((a) => clamp(a + 1))}
              className={cn(
                MOBILE_HIT_AREA,
                'absolute right-1 top-1/2 inline-flex size-6 -translate-y-1/2 items-center justify-center rounded-full bg-background/85 text-foreground/80 shadow-elevation-1 backdrop-blur-sm transition-colors hover:bg-background',
              )}
            >
              <CaretRightIcon className="size-3.5" weight="bold" />
            </button>
          </>
        ) : null}
      </div>
      {multi ? (
        <div className="flex items-center justify-center gap-1" aria-hidden>
          {items.map((_, i) => (
            <span
              key={i}
              className={cn(
                'size-1.5 rounded-full transition-colors',
                i === activeIdx ? 'bg-skill' : 'bg-skill/25',
              )}
            />
          ))}
        </div>
      ) : onSeeMore && !busy ? (
        <button
          type="button"
          onClick={onSeeMore}
          className="block w-full text-center text-detail text-skill/70 underline decoration-transparent underline-offset-2 transition-colors hover:text-skill hover:decoration-current"
        >
          See more options
        </button>
      ) : null}
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="flat"
          className="flex-1 bg-skill/10 text-skill hover:bg-skill/15"
          disabled={busy}
          onClick={() => onPick(current)}
        >
          <CheckIcon className="size-3.5" weight="bold" />
          Use this
        </Button>
        <Button
          size="sm"
          variant="flat"
          className="hover:text-foreground"
          disabled={busy}
          onClick={onDismiss}
        >
          Discard
        </Button>
      </div>
    </div>
  );
}
